// Reference-document indicator (Session AFP, Stream A2): a small, self-fetching line shown on a
// training session (its detail page and the create modal) telling the office whether a
// customer-facing reference document is on file for that session's training type. It is the
// "check the right document exists per training type" cue at the point of booking, and it links
// straight to the library to review or add one. Read-only: it never changes a session.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import type { TrainingType, TrainingCoverage } from "../../lib/trainingTypes";

export function TrainingReferenceIndicator({ trainingType, className = "" }: { trainingType: TrainingType; className?: string }) {
  const [coverage, setCoverage] = useState<TrainingCoverage | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    // Promise.resolve().then(...) so even a synchronous throw (a partially-mocked api in a test, or
    // any runtime hiccup) becomes a caught rejection: this indicator never breaks its host page.
    Promise.resolve()
      .then(() => api.trainingDocuments.coverage())
      .then((r) => {
        if (live) setCoverage(r.coverage);
      })
      .catch(() => {
        if (live) setFailed(true);
      });
    return () => {
      live = false;
    };
  }, []);

  // Stay silent until we know, and if the lookup fails (never the point of this page): a training
  // session must never be blocked by a reference-library hiccup.
  if (failed || !coverage) return null;

  const forType = coverage[trainingType];
  const hasCustomerFacing = (forType?.customerFacing ?? 0) > 0;

  return (
    <div
      className={`rounded-ele border px-3 py-2 text-sm ${hasCustomerFacing ? "border-ele-green/50 text-ele-body" : "border-[var(--ele-amber-text)]/40 text-ele-body"} ${className}`}
      role="note"
    >
      {hasCustomerFacing ? (
        <>
          A customer-facing reference document is on file for this training type.{" "}
          <Link to="/office/training-documents" className="text-ele-green hover:underline">
            View reference library
          </Link>
        </>
      ) : (
        <>
          No customer-facing reference document on file for this training type.{" "}
          <Link to="/office/training-documents" className="text-ele-green hover:underline">
            Add one
          </Link>
        </>
      )}
    </div>
  );
}
